import React, { useMemo } from "react";
import Button from "@mui/material/Button";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { EnrichmentSynchronizationState } from "../types";
import {
  SYNC_SALESFORCE_BUTTON_TEXTS,
  UNAVAILABLE_SYNC_STATES,
} from "../consts";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutline from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { LightTooltip } from "../../../components/LightTooltip";
import { useSalesforceIntegrationDetailsContext } from "../contexts/useSalesforceIntegrationDetailsContext";
import Fade from "@mui/material/Fade";

// Internal tooltip component
const SyncButtonTooltip: React.FC<{
  state?: EnrichmentSynchronizationState;
  hasValidConfiguration: boolean;
}> = ({ state, hasValidConfiguration }) => {
  if (
    hasValidConfiguration &&
    state &&
    SYNC_SALESFORCE_BUTTON_TEXTS.tooltips[
      state as keyof typeof SYNC_SALESFORCE_BUTTON_TEXTS.tooltips
    ]
  ) {
    const config =
      SYNC_SALESFORCE_BUTTON_TEXTS.tooltips[
        state as keyof typeof SYNC_SALESFORCE_BUTTON_TEXTS.tooltips
      ];
    if (typeof config === "object" && "title" in config) {
      return (
        <Box>
          <Typography fontWeight={600}>{config.title}</Typography>
          <Typography variant="body2">{config.description}</Typography>
        </Box>
      );
    }
  }

  if (!hasValidConfiguration) {
    const config = SYNC_SALESFORCE_BUTTON_TEXTS.tooltips.invalidSettings;
    return (
      <Box>
        <Typography fontWeight={600}>{config.title}</Typography>
        <Typography variant="body2">{config.description}</Typography>
      </Box>
    );
  }

  return null;
};

// Main component
export const SyncSalesforceButton: React.FC = () => {
  const {
    integration,
    synchronizationState,
    synchronizationLoading,
    productsSelection,
    categoriesSelection,
    handleSync,
    isInitiatingSync,
  } = useSalesforceIntegrationDetailsContext();

  // Sync button logic (inline)
  const hasExportSettings = useMemo(() => {
    return (
      integration.syncSettings.exportTechInstalls ||
      integration.syncSettings.exportItSpend ||
      integration.syncSettings.exportFirmographics ||
      integration.syncSettings.exportAccountScoring
    );
  }, [integration]);

  const hasSelections = useMemo(() => {
    return (
      productsSelection.value.length > 0 || categoriesSelection.value.length > 0
    );
  }, [productsSelection.value, categoriesSelection.value]);

  const syncStatus = useMemo(() => {
    return {
      isUnavailable: synchronizationState
        ? UNAVAILABLE_SYNC_STATES.includes(synchronizationState)
        : false,
      isInProgress:
        synchronizationState ===
        EnrichmentSynchronizationState.SYNC_IN_PROGRESS,
      state: synchronizationState,
    };
  }, [synchronizationState]);

  const isDisabled =
    syncStatus.isUnavailable || (!hasExportSettings && !hasSelections);
  const hasValidConfiguration = hasExportSettings || hasSelections;
  const { isInProgress } = syncStatus;
  const syncState = syncStatus.state;

  const isLoading = synchronizationLoading || isInitiatingSync || isInProgress;
  const tooltipEnabled = isDisabled || !hasValidConfiguration;
  const TooltipIcon = isInProgress ? InfoOutline : ErrorOutlineIcon;

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Button
        data-testid="salesforce-sync-btn"
        variant="contained"
        color="primary"
        endIcon={
          isLoading ? (
            <CircularProgress color="inherit" size={18} thickness={4} />
          ) : undefined
        }
        disabled={isDisabled || isLoading}
        onClick={handleSync}
        sx={{
          "&.Mui-disabled": {
            backgroundColor: "primary.main",
            opacity: 0.5,
            color: "white",
          },
        }}
      >
        {isInProgress
          ? SYNC_SALESFORCE_BUTTON_TEXTS.labels.inProgress
          : SYNC_SALESFORCE_BUTTON_TEXTS.labels.default}
      </Button>

      {tooltipEnabled && (
        <Fade in={true} timeout={500}>
          <LightTooltip
            title={
              <SyncButtonTooltip
                state={syncState}
                hasValidConfiguration={hasValidConfiguration}
              />
            }
          >
            <TooltipIcon
              sx={(theme) => ({
                fontSize: 24,
                color: isInProgress
                  ? theme.palette.secondary.main
                  : theme.palette.error.main,
              })}
            />
          </LightTooltip>
        </Fade>
      )}
    </Stack>
  );
};
